import { ImportFileApiClients, PolicyDependenciesContainer } from '../../dependencies/PolicyDependenciesContainer';
import { translateFn } from '../../../../../../utils/translator.helper';
import { GzDialogWebComponent } from '../../../../../../ui/gzDialog';
import { GzButtonWebComponent } from '../../../../../../ui/gzButton';
import { GzToastNotificationWebComponent } from '../../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../../ui/gzToastNotification/type';
import { GzPolicyUiSettingsImportFileInputWebComponent } from './browseFile';
import { WEB_COMPONENT_POLICY_PANEL_IMPORT_FILE_INPUT } from '../../../../../../webcomponents.constants';
import { PolicyImportEvents } from './browseFile/events/events';

export class GzPolicyUiSettingsImportDialog {
  private maxErrorNumberToDisplay = 10;
  private readonly clients: ImportFileApiClients;
  private dialog: GzDialogWebComponent;

  private translations: {
    title: string;
    fileInput: {
      label: string;
      emptyError: string;
    };
    validationMessages: {
      valid: string;
      invalid: {
        oneError: Function;
        multipleErrors: Function;
      };
    };
  };

  public constructor(
    private importFn: (file: File) => Promise<any>,
    private validateImportedItems: Function,
    customTranslations?: Object
  ) {
    this.computeTranslations(customTranslations);
  }

  private computeTranslations(customTranslations?) {
    this.translations = {
      title: customTranslations?.title ?? translateFn('Import exclusions'),
      fileInput: {
        label: customTranslations?.fileInput?.label ?? translateFn('Exclusions file'),
        emptyError: customTranslations?.fileInput?.emptyError ?? translateFn('No file selected.'),
      },
      validationMessages: {
        valid: customTranslations?.validationMessages?.valid ?? translateFn('Exclusions successfully imported.'),
        invalid: {
          oneError:
            customTranslations?.validationMessages?.invalid?.oneError ??
            ((rowWithErrors: number[]) =>
              translateFn('The exclusion on this invalid line was not imported: {rowWithErrors}', {
                rowWithErrors: rowWithErrors.join(', '),
              })),
          multipleErrors:
            customTranslations?.validationMessages?.invalid?.multipleErrors?.fewerThanMax ??
            ((numberOfErrors: number, rowWithErrors: number[]) =>
              translateFn('Exclusions on {numberOfErrors} invalid lines were not imported: {rowWithErrors}', {
                numberOfErrors: numberOfErrors,
                rowWithErrors: rowWithErrors.join(', '),
              })),
        },
      },
    };
  }

  public show() {
    this.dialog = GzDialogWebComponent.computeCustomDialog(
      this.getWindowBody(),
      this.getWindowFooter(),
      this.getWindowTitle(),
      null,
      this.confirmCallback.bind(this),
      null,
      false
    );
  }

  private async confirmCallback() {
    // get input file component
    const body = this.dialog.shadowRoot?.querySelector(
      WEB_COMPONENT_POLICY_PANEL_IMPORT_FILE_INPUT
    ) as GzPolicyUiSettingsImportFileInputWebComponent;
    if (!body) {
      return;
    }

    // get file from input
    const file = body.getValue();
    if (!file) {
      body.setErrorMessage(this.translations.fileInput.emptyError);
      return;
    }

    // set loading state on confirm button
    const confirmBtn = this.dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm') as GzButtonWebComponent;
    confirmBtn.setLoadingState(true);

    try {
      // try to import csv file
      const response = await this.importFn(file[0]);
      if (!response || !response.success) {
        this.buildToastMessage(
          response.message || response.data || this.getGenericErrorToastMessage(),
          ToastSeverity.ERROR
        );
        return;
      }

      // validate imported items
      const { valid, rowWithErrors } = await this.validateImportedItems(response.data);

      // show success/warning toast notification
      this.buildToastMessage(
        this.getMessageToDisplayAfterValidation(valid, rowWithErrors),
        valid ? ToastSeverity.SUCCESS : ToastSeverity.WARNING,
        !valid
      );
    } catch (error) {
      // show error toast notification
      this.buildToastMessage(this.getGenericErrorToastMessage(), ToastSeverity.ERROR);
    } finally {
      this.dialog.close();
    }
  }

  private getGenericErrorToastMessage(): string {
    return translateFn(
      'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
    );
  }

  private getMessageToDisplayAfterValidation(valid: boolean, rowWithErrors: number[]) {
    // is valid
    if (valid) {
      return this.translations.validationMessages.valid;
    }

    const numberOfErrors = rowWithErrors.length;
    if (numberOfErrors === 1) {
      return this.translations.validationMessages.invalid.oneError(rowWithErrors);
    }

    // has fewer errors than max number to display
    const hasMoreErrors = rowWithErrors.length > this.maxErrorNumberToDisplay;
    if (!hasMoreErrors) {
      return this.translations.validationMessages.invalid.multipleErrors(numberOfErrors, rowWithErrors);
    }

    // has more errors than max number to display
    rowWithErrors = rowWithErrors.slice(0, this.maxErrorNumberToDisplay);
    return this.translations.validationMessages.invalid.multipleErrors(numberOfErrors, rowWithErrors) + '...';
  }

  private buildToastMessage(message: string, severity: ToastSeverity, keepOpen = false) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: severity,
      keepOpen: keepOpen,
    });
  }

  private getWindowBody(): GzPolicyUiSettingsImportFileInputWebComponent {
    const body = new GzPolicyUiSettingsImportFileInputWebComponent();
    body.inputLabel = this.translations.fileInput.label;
    body.buttonLabel = translateFn('Browse');

    body.addEventListener(PolicyImportEvents.OnImportFileNameChange, (event: CustomEvent) => {
      // set/remove error message
      if (!event.detail) {
        body.setErrorMessage(this.translations.fileInput.emptyError);
      } else {
        body.removeErrorMessage();
      }
    });

    return body;
  }

  private getWindowFooter(): string {
    return `<gz-button id='confirm'>
       ${translateFn('Import')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;
  }

  private getWindowTitle(): string {
    return this.translations.title;
  }
}
